import { useCallback, useMemo } from "react";
import { Outlet, useParams } from "react-router-dom";
import type { DocumentFlowConfig } from "./DocumentFlowContext";
import { DocumentFlowContext } from "./DocumentFlowContext";
import { useAddPrescriptionDetail, useRequestData } from "@hooks";
import { queryClient } from "./queryClient";
import type { PrescriptionDetail } from "@interfaces";

export function PrescriptionFlowProvider() {
  const { requestId = "" } = useParams();
  const { mutateAsync: addDetail } = useAddPrescriptionDetail();
  const { data: requestData } = useRequestData();

  const submitDetail = useCallback(
    async (detail: PrescriptionDetail) => {
      await addDetail({ requestId, prescriptionDetail: detail });
      await queryClient.invalidateQueries({
        queryKey: ["request", requestId],
      });
    },
    [addDetail, requestId],
  );

  const config: DocumentFlowConfig = useMemo(
    () => ({
      routes: {
        searchBackRoute: `/app/solicitudes/${requestId}/receta/crear`,
        treatmentRoute: `/app/solicitudes/${requestId}/receta/crear/tratamiento`,
        treatmentBackRoute: `/app/solicitudes/${requestId}/receta/crear/buscar`,
        afterSubmitRoute: `/app/solicitudes/${requestId}/receta/crear`,
      },
      labels: {
        treatmentTitle: "Detalle tratamiento",
        submitButtonText: undefined,
        searchTitle: "Busca el medicamento que quieres recetar",
        mobileSearchTitle: "¿Qué medicamento quieres recetar?",
      },
      submitDetail,
      enableInteractions: true,
      showTemplatesSection: !requestData?.details?.length,
    }),
    [requestId, submitDetail, requestData],
  );

  return (
    <DocumentFlowContext.Provider value={config}>
      <Outlet />
    </DocumentFlowContext.Provider>
  );
}
